if(!customElements.get('gallery-popup')){
    class GalleryPopup extends HTMLElement {
        constructor(){
            super();

            this.currentIndex = 1;

            // close popup
            this.querySelectorAll('[data-close]').forEach(item => {
                item.addEventListener('click', this.close.bind(this));
                item.addEventListener('keydown', e => {
                    if(e.code === 'Enter') this.close();
                });
            });
            this.addEventListener('keyup', e => {
                if(e.code && e.code.toUpperCase() === 'ESCAPE') this.close();
            });
        }

        async initializeSwiper(){
            this.swiperContainer = this.querySelector('.swiper-container');
            if(!this.swiperContainer) return;

            this.slides = this.querySelectorAll('.swiper-slide');
            if(this.slides.length <= 1) return;

            if(typeof Swiper === 'undefined'){
                await new Promise(resolve => {
                    const check = () => {
                        if(typeof Swiper !== 'undefined') resolve();
                        else requestAnimationFrame(check);
                    };
                    check();
                });
            }

            var swiperSettings = {
                init: false,
                initialSlide: 0,
                loop: false,
                centeredSlides: false,
                speed: 500,
                watchOverflow: true,
                observer: true,
                observeSlideChildren: true,
                breakpoints: {
                    0: {slidesPerView: 1}
                },
                on: {
                    init: () => {
                        this.closeMedia();
                        this.startMedia();

                        // pagination
                        const paginationBullets = this.querySelectorAll(".swiper-pagination-bullet");
                        if(paginationBullets.length > 0){
                            this.style.setProperty('--pagination-width', `calc(100% / ${paginationBullets.length})`);
                            paginationBullets.forEach((item, index) => {
                                item.setAttribute('data-index', `${index}`);
                            });
                            setTimeout(() => {
                                this.querySelector('.swiper-pagination')?.classList.add('loaded');
                                this.checkSwiperLock();
                            }, 500);
                        }
                    },
                    slideChange: () => {
                        this.currentIndex = this.swiper.activeIndex;
                        this.closeMedia();
                        this.startMedia();

                        // pagination
                        const activePagination = this.querySelector(".swiper-pagination-bullet-active");
                        if(activePagination){
                            const activePaginationIndex = parseInt(activePagination.dataset.index);
                            this.style.setProperty('--pagination-translate-x', `calc(100% * ${activePaginationIndex})`);
                        }
                    }
                }
            }

            if(this.swiperContainer.querySelector(".swiper-navigation-wrapper")){
                swiperSettings.navigation = {
                    nextEl: this.swiperContainer?.querySelector('.button-next'),
                    prevEl: this.swiperContainer?.querySelector('.button-previous')
                }
            }

            if(this.querySelector('.swiper-pagination')){
                swiperSettings.pagination = {
                    el: this.querySelector('.swiper-pagination')
                }
            }

            this.swiper = new Swiper(this.swiperContainer, swiperSettings);
            this.swiper.init();

            // Mark clone slides as inert so keyboard focus skips duplicates
            this.swiper.slides.forEach(slide => {
                if(slide.classList.contains('swiper-slide-duplicate')){
                    slide.setAttribute('inert', '');
                    slide.setAttribute('aria-hidden', 'true');
                }
            });

            // Re-apply inert to clones after Swiper updates (loop mode re-creates clones)
            this.swiper.on("slideChange", () => {
                this.swiper.slides.forEach(slide => {
                    if(slide.classList.contains('swiper-slide-duplicate')){
                        slide.setAttribute('inert', '');
                        slide.setAttribute('aria-hidden', 'true');
                    }
                });
            });
            this.swiper.on("resize", this.checkSwiperLock.bind(this));

            this.setupFocusListeners();
        }

        checkSwiperLock(){
            if(!this.querySelector(".swiper-pagination")) return;
            
            if(this.swiper.isLocked){
                this.querySelector(".swiper-pagination").classList.add('hidden');
            }else{
                this.querySelector(".swiper-pagination").classList.remove('hidden');
            }
        }

        setupFocusListeners(){
            this.querySelectorAll(".button-next, .button-previous").forEach(button => {
                button.addEventListener('focus', () => {
                    button.classList.add('focused');
                    button.addEventListener('blur', () => {
                        button.classList.remove('focused');
                    }, { once: true });
                });
            });
        }

        open(triggerElement = null){
            this.previouslyFocusedElement = triggerElement || document.activeElement;

            document.body.style.overflow = 'hidden';
            document.documentElement.style.overflow = 'hidden';

            this.setAttribute('data-hidden', 'false');
            this.removeAttribute('inert');

            FocusTrap.activate(this);

            if(!this.swiper){
                this.initializeSwiper();
            }else{
                this.swiper.update();
            }

            this.updateSlide();
            this.addFocus();
        }

        updateSlide(){
            if(!this.swiper) return;

            this.swiper.slideTo(this.currentIndex, 0, false);
        }

        close(){
            this.closeMedia();

            FocusTrap.deactivate(this);

            this.setAttribute('inert', '');
            this.classList.add('closed');
            setTimeout(() => {
                this.setAttribute('data-hidden', 'true');

                if(this.previouslyFocusedElement){
                    this.previouslyFocusedElement.focus();
                }
            }, 0);

            document.body.style.overflow = 'initial';
            document.documentElement.style.overflow = 'initial';
        }

        addFocus(){
            const focusableElements = this.querySelectorAll('input, button, [tabindex]');
            if(focusableElements.length > 0){
                setTimeout(() => {
                    focusableElements[0].focus();
                }, 100);
            }
        }

        startMedia(){
            const currentSlide = this.querySelector(`.swiper-slide[data-item-index="${this.currentIndex + 1}"]`);
            const video = currentSlide.querySelector('video');
            if(video){
                video.classList.add('loaded');
                setTimeout(() => {
                    video.play();
                }, 50);
            }
        }

        closeMedia(){
            this.querySelectorAll('.swiper-slide').forEach(item => {
                const video = item.querySelector('video');
                if(video){
                    video.classList.remove('loaded');
                    video.pause();
                    video.currentTime = 0;
                }
            });
        }
    }

    customElements.define('gallery-popup', GalleryPopup);
}